import { Button, Label, TextInput } from 'flowbite-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateLeadGen, useCreateLeadGenSource, useGetAllLeadGenSource } from 'src/hooks/leadGen.hooks';
import Select, { SingleValue } from 'react-select';
import { Source, LeadData, LeadGensUser, } from 'types';
import { useToast } from 'src/context/ToastProvider';
import { useMutation, useQueryClient } from 'react-query';
import CreatableSelect from 'react-select/creatable';
import { validateName } from 'shared-utils';
import {
	validateUrl,
	validateEmailAddress,
	validatePhoneNumber,
} from 'shared-utils';
import { getMyCompaniesListQuery } from 'src/apis/company';
import DynamicFieldSelection from '../position/DynamicFieldSelection';

interface Props {
	onClose: () => void;
	refetchForm?: () => void;
	allLeadGensUser?: LeadGensUser[];
	curr_user?: {
		id: string,
		name: string
	}
}

const CreateLeadGenForm: React.FC<Props> = ({ onClose, refetchForm, allLeadGensUser, curr_user }) => {
	const navigate = useNavigate();
	const toast = useToast();
	const queryClient = useQueryClient();

	const { data: allLeadGensSource, refetch } = useGetAllLeadGenSource();

	const { mutate: createLeadGen, isLoading } = useCreateLeadGen(
		data => {
			toast({ title: 'Lead created successfully', type: 'success' });
			onClose();
			refetchForm?.();
			navigate('/leads-gen');
			queryClient.invalidateQueries('leadGens');
		},
		error => {
			toast({ title: 'Error creating lead', type: 'error' });
		},
	);
	const { mutate: createLeadGenSource } = useCreateLeadGenSource(
		data => {
			toast({ title: 'Lead created successfully', type: 'success' });
			// onClose();
			refetch?.();
			// navigate('/leads-gen');
			queryClient.invalidateQueries('leadGens');
		},
		error => {
			toast({ title: 'Error creating lead', type: 'error' });
		},
	);

	const [formError, setFormError] = useState<any>({});
	const [formData, setFormData] = useState<LeadData>({
		sourceCompanyName: '',
		companyUrl: '',
		name: '',
		email: '',
		phone: 0,
		jobPostingLink: '',
		leadGenPOC: curr_user?.id || '',
		source: Source.GoogleAds,
	});

	const [companyOptions, setCompanyOptions] = useState<{ name: string; value: string }[]>([]);

	const fetchCompanyNameFn = useMutation(['getMyCompaniesListQuery'], getMyCompaniesListQuery, {
		onSuccess(data) {
			setCompanyOptions(data.companies?.map(c => ({ name: c.name, value: c.name })) ?? []);

		},
	});
	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		let error = '';

		if (name === 'email') {
			if (!validateEmailAddress(value)) {
				error = 'Invalid Email';
			}
		} else if (name === 'phone') {
			if (!validatePhoneNumber(value)) {
				error = 'Invalid Phone';
			}
		} else if (name === 'jobPostingLink' || name === 'companyUrl') {
			if (!validateUrl(value)) {
				error = 'Invalid URL';
			}
		} else if (name === "sourceCompanyName") {
			if (!validateName(value)) {
				error = 'Invalid Name';

			}
		} else if (name === "name") {
			if (!validateName(value)) {
				error = 'Invalid Name'
			}
		}

		setFormError((prevError: any) => ({
			...prevError,
			[name]: error,
		}));

		setFormData(prevData => ({
			...prevData,
			[name]: name === 'dateOfLeadAssigned' || name === 'lastCallLogTimestamp' ? new Date(value) : value,
		}));
	};

	const handleSelectChange = (newValue: SingleValue<{ label: Source; value: Source }>) => {
		if (newValue) {
			setFormData(prevData => ({
				...prevData,
				source: newValue.value,
			}));
		}
	};
	const handleCreateSource = (inputValue: string) => {
		createLeadGenSource({ sourceName: inputValue })
		setFormData(prevData => ({
			...prevData,
			source: inputValue as any,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validateEmailAddress(formData.email)) {
			toast({ title: 'Invalid email format', type: 'error' });
			return;
		}
		if (!validateUrl(formData.jobPostingLink || '')) {
			toast({ title: 'Invalid URL format', type: 'error' });
			return;
		}
		if (!validatePhoneNumber(formData.phone.toString())) {
			toast({ title: 'Phone number must be 10 digits', type: 'error' });
			return;
		}
		const payload = {
			...formData,
		};
		try {
			createLeadGen(payload);
		} catch (error) {
			console.error('Error creating lead:', error);
		}
	};

	const labelStyle = `dark:text-white text-[#111928]`;

	return (
		<form className="flex flex-col gap-4 px-8 py-5" onSubmit={handleSubmit}>
			<div className="grid grid-cols-2 gap-2">
				<div className="mb-2 block tmd:flex-1 w-full">
					<div className="mb-[5px]">
						<Label value="Company" />
						<span className="text-[red]">*</span>
					</div>
					<div>
						<DynamicFieldSelection
							onChange={e => {
								setFormData((prevData) => ({
									...prevData,
									sourceCompanyName: e,
								}));
							}}
							onType={e => fetchCompanyNameFn.mutate(e)}
							options={companyOptions}
							placeholder="Select a Company (Type to search)"
							creatable={true}
						/>
					</div>
				</div>

				<div className="mb-2 block">
					<div>
						<Label htmlFor="url" value="Company URL" className={`${labelStyle}`} />

					</div>
					<TextInput
						id="companyUrl"
						name="companyUrl"
						type="text"
						value={formData.companyUrl || ''}
						onChange={handleChange}
						placeholder="https://www.levelup.masaischool.com"
					/>
					{formError.companyUrl && <p className="text-red-600">{formError.companyUrl}</p>}
				</div>
				<div className="mb-2 block">
					<div>
						<Label>{'Contact Name'}</Label>
						<span className="text-red-600 pl-1">*</span>
					</div>
					<TextInput
						id="name"
						name="name"
						type="text"
						value={formData.name || ''}
						onChange={handleChange}
					/>
					{formError.name && <p className="text-red-600">{formError.name}</p>}
				</div>
				<div className="mb-2 block">
					<div>
						<Label>{'Email'}</Label>
						<span className="text-red-600 pl-1">*</span>
					</div>
					<TextInput
						id="email"
						name="email"
						type="email"
						placeholder='user@gmail.com'
						value={formData.email || ''}
						onChange={handleChange}
					/>
					{formError.email && <p className="text-red-600">{formError.email}</p>}
				</div>
			</div>
			<div className="grid grid-cols-2 gap-2">
				<div className="mb-2 block">
					<div>
						<Label>{'Phone'}</Label>
						<span className="text-red-600 pl-1">*</span>
					</div>
					<TextInput
						type="text"
						id="phone"
						name="phone"
						onChange={handleChange}
						value={formData.phone || ''}
					/>
					{formError.phone && <p className="text-red-600">{formError.phone}</p>}
				</div>
				<div className="mb-2 block">
					<div>
						<Label>{'Job Posting Link'}</Label>
						<span className="text-red-600 pl-1">*</span>
					</div>
					<TextInput
						id="jobPostingLink"
						name="jobPostingLink"
						type="text"
						placeholder="https://www.levelup.masaischool.com"
						value={formData.jobPostingLink || ''}
						onChange={handleChange}
					/>
					{formError.jobPostingLink && <p className="text-red-600">{formError.jobPostingLink}</p>}
				</div>
			</div>
			<div className="grid grid-cols-2 gap-2">
				<div className="mb-2 block">
					<div>
						<Label>{'Lead Gen POC'}</Label>
						<span className="text-red-600 pl-1">*</span>
					</div>
					<Select
						instanceId="leadGenPOC"
						menuPlacement={'auto'}
						classNamePrefix="my-select-plans"
						name="leadGenPOC"
						className="single-value-select-tag"
						options={allLeadGensUser?.map(user => ({
							label: user.name,
							value: user._id,
						})) || []}
						placeholder="Select Lead Gen POC"
						value={allLeadGensUser?.find(user => user._id === formData.leadGenPOC) ? {
							label: allLeadGensUser.find(user => user._id === formData.leadGenPOC)?.name,
							value: formData.leadGenPOC,
						} : {
							label: curr_user?.name || '',
							value: curr_user?.id || '',
						}}
						onChange={(newValue) => {
							if (newValue) {
								setFormData(prevData => ({
									...prevData,
									leadGenPOC: newValue.value,
								}));
							}
						}}
					/>
				</div>
				<div className="mb-2 block">
					<div>
						<Label>{'Source'}</Label>
						<span className="text-red-600 pl-1">*</span>
					</div>
					<CreatableSelect
						components={{
							DropdownIndicator: null,
						}}
						menuPlacement='top'
						isClearable
						options={allLeadGensSource?.data?.map((source: any) => ({ label: source.sourceName || '', value: source.sourceName || '' })) || [] as any}
						placeholder="Select or create a source"
						value={formData.source ? { label: formData.source, value: formData.source } : null}
						onChange={handleSelectChange}
						onCreateOption={handleCreateSource}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
					/>
				</div>
			</div>



			<div className="grid grid-cols-3 gap-2">
				<div className="mt-4">
					<Button
						color="purplePlaced"
						type="submit"
						disabled={
							!formData.name ||
							!formData.sourceCompanyName ||
							// !formData.companyUrl ||
							// !formData.email ||
							!formData.phone ||
							!formData.jobPostingLink ||
							!formData.leadGenPOC ||
							!formData.source ||
							!!formError.email ||
							!!formError.phone ||
							!!formError.jobPostingLink
							// !!formError.companyUrl
						}
					>
						{isLoading ? 'Creating...' : 'Create Lead'}
					</Button>
				</div>
			</div>
		</form>
	);
};

export default CreateLeadGenForm;